import type React from 'react';

import { useGetFace, useGetCard, useGetLists } from '@vlabs/api/artemy/client';
import type { CardId } from '@vlabs/api/artemy/model';
import type { FaceId } from '@vlabs/api/artemy/model/FaceId';
import { DetectionIcon } from '@vlabs/icons';
import { useHasPermission, usePageAccess } from '@vlabs/permissions';
import { SimilarityTag } from '@vlabs/shared/components/similarity-tag/SimilarityTag';
import { TruncatedText } from '@vlabs/shared/components/truncated-text/TruncatedText';
import { formatDateTime, DATE_FORMATS } from '@vlabs/shared/utils/date';
import { FaceSample, Tag } from '@vlabs/uikit';
import { Link } from '@vlabs/uikit/components/controls/link/Link';
import { routes } from '@vlapps/point3/src/app/routes';
import { generatePath } from 'react-router-dom';

import { GoToCardLink } from '@vlabs/monokit/features/go-to-card';

import st from './CardCard.module.sass';
import { useCardCardTranslation } from './i18n';

interface CardCardPoint3Props {
  faceId: FaceId;
  similarity?: number;
}

function CardCardPoint3({
  faceId,
  similarity = undefined,
}: CardCardPoint3Props): React.JSX.Element | null {
  const { t } = useCardCardTranslation();
  const canReadLists = useHasPermission('list', 'read');
  const canViewListDetails = usePageAccess('lists');

  const { data: face } = useGetFace(
    faceId,
    { targets: ['face_id', 'card_id', 'avatar', 'user_data', 'create_time'] },
  );
  const { data: card } = useGetCard(
    face?.card_id as CardId,
    { targets: ['user_data', 'lists'] },
    { query: { enabled: !!face?.card_id } },
  );

  const listIds = card?.lists ?? [];

  const { data: lists } = useGetLists(
    { list_ids: listIds, page_size: 1000 },
    { query: { enabled: canReadLists && listIds.length > 0 } },
  );

  if (!face?.card_id) return null;

  const cardFullName = (card?.user_data?.fio as string | undefined)?.trim();
  const fullName = cardFullName?.length ? cardFullName : (face.user_data ?? '-');

  const listItems = lists?.lists ?? [];
  const listNames = listItems.map(({ list_id, user_data }) => {
    const name = user_data.trim();
    if (!name) return list_id;
    return name;
  });

  const listLinks = listItems.map(({ list_id }, index) => (
    <span key={list_id}>
      <Link
        className={st.ListLink}
        data-testid={list_id}
        to={generatePath(routes.lists.read, { listId: list_id })}
      >
        {listNames[index]}
      </Link>
      {index < listItems.length - 1 && ', '}
    </span>
  ));

  let listsValue: React.ReactNode;

  if (canViewListDetails && listItems.length > 0) {
    listsValue = (
      <TruncatedText
        clickable
        hasTruncation
        tooltipContent={listLinks}
      >
        {listLinks}
      </TruncatedText>
    );
  } else if (listItems.length > 0) listsValue = listNames.join(', ');
  else listsValue = '-';

  return (
    <div className={st.CardCard}>
      <div className={st.PrimarySection}>
        <FaceSample
          className={st.Sample}
          src={face.avatar ? `/api/${face.avatar}` : undefined}
        />

        <div className={st.Details}>
          <Tag
            kind="neutral"
            size="large"
            value={t('match_tag')}
          />

          {!!similarity && (
            <SimilarityTag
              icon={<DetectionIcon />}
              kind="positive"
              size="large"
              title={t('similarity_tag')}
              value={similarity}
            />
          )}

          <div className={st.Date}>{formatDateTime(face.create_time, DATE_FORMATS.withYear)}</div>
        </div>
      </div>

      <div className={st.SecondarySection}>
        <div className={st.RowItem}>
          <div className={st.RowTitle}>{t('full_name')}</div>
          <div className={st.RowValue}>{fullName}</div>
        </div>

        {!!canReadLists && (
          <div className={st.RowItem}>
            <div className={st.RowTitle}>{t('lists')}</div>
            <div className={st.RowValue}>{listsValue}</div>
          </div>
        )}
      </div>

      <GoToCardLink
        cardId={face.card_id}
        className={st.Link}
      />
    </div>
  );
}

export { CardCardPoint3 };
